import React from 'react';
import './TicketPage.scss';
import Spinner from 'Components/Shared/Spinner/Spinner';
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl';
import PriceItem from 'Components/Shared/PriceItem/PriceItem';
import FormattedText from 'Components/Shared/FormattedText/FormattedText';
import FetchFailureMessage from 'Components/Shared/FetchFailureMessage/FetchFailureMessage';
import { OrderItems } from 'Components/OrderItemSummary/OrderItemSummary';
import moment from 'moment';

interface IProps {
  isFetching: boolean;
  isFetchFailed: boolean;
  isTicketNotFound: boolean;
  orderID: number;
  ticketSummary: Julius.Website.TicketSummaryViewModel;
}

const TicketPage: React.SFC<IProps> = ({
  isFetching,
  isFetchFailed,
  isTicketNotFound,
  orderID,
  ticketSummary,
}) => {

  if (isFetching) {
    return <Spinner style='ticket-page' />;
  }

  if (isTicketNotFound) {
    return (
      <span className='ticket-page error'>
        <FormattedMessage 
          id='ticket.notfound'
          defaultMessage='Kunde inte inte hitta någon biljett.'
          values={ { orderID } }
        />
      </span>
    );
  }

  if (isFetchFailed || !ticketSummary) {
    return <FetchFailureMessage />;
  }

  const { showName, showImageUrl, performanceCity, performanceVenue, 
    performanceDate, customerName, seatTickets, packageTickets, orderBarcode, 
    orderItems, info, totalPrice, vatPrice, servicePrice,
    freetext2, freetext3, freetext4 } = ticketSummary;

  const getAdditionalInfo = () => {
    return { __html: [freetext3, freetext4].filter(x => x).join(" ") };
  }

  const getAdditionalInfoForPt = (pt: Julius.Website.TicketPackageViewModel) => {
    return { __html: [pt.freetext3, pt.freetext4].filter(x => x).join(" ") };
  }

  const seatCountByDiscount = seatTickets.reduce((countMap, seat) => {
    countMap[seat.discountName] = countMap[seat.discountName] || 0;
    countMap[seat.discountName] = countMap[seat.discountName] + 1;
    return countMap;
  }, {});

  return (
    <div className='ticket-page'>
      <div className='ticket-page-content'>
        <img className='show-image' alt={ showName } src={ showImageUrl } />
        <div className='order-overview'>
          <div className='show-name'>
            { showName }
            { freetext2 &&
              <div className='show-subtitle' dangerouslySetInnerHTML={{ __html: freetext2 }}/>
            }
          </div>
          { performanceVenue && performanceCity &&
            <div className='show-location'>
              { performanceVenue }, { performanceCity }
            </div>
          }
          { performanceDate.getFullYear() !== 1 &&
            <div className='show-date'>
              <FormattedDate 
                value={ performanceDate }
                month='long'
                day='numeric'
                year='numeric'
              />,&nbsp; 
              <FormattedTime 
                value={ performanceDate }
                hour='numeric'
                minute='numeric'
              />
            </div>
          }
          <div className='customer-name'>
            <FormattedText
              displayName='Namn'
              formattedName='ticket.name'
            />: { customerName }
          </div>
          <div className='order-id'>
            <FormattedText
              displayName='Bokningsnummer'
              formattedName='ticket.orderID'
            />: { orderID }
          </div>
          <div className='additional-info' dangerouslySetInnerHTML={ getAdditionalInfo() }/>
          <div className='tickets-title'>
            <FormattedText 
              displayName='Biljetter'
              formattedName='ticket.tickets'
            />
          </div>
          <div className='tickets-summary'>
            { Object.keys(seatCountByDiscount).map((discount, key) => 
              <div key={ key }
                >{ seatCountByDiscount[discount] } á { discount }
              </div>)
            }
          </div>
          <img
            alt='Biljett QR-kod'
            className='barcode-image' 
            src={ `${ process.env.REACT_APP_API_URL }/api/julius/TicketQR?code=SEBJ ${ orderBarcode }` } 
          />
        </div>
        <div className='order-spec'>
          <div className='order-spec-title'>
            <FormattedText
              displayName='Specifikation'
              formattedName='ticket.specification'
            />
          </div>
          <OrderItems items={ orderItems } />
          <div className='prices'>
            <div className='total'>
              <FormattedText
                displayName='Totalt'
                formattedName='ticket.price.total'
              />
              <PriceItem price={ totalPrice } decimals={ 2 } />
            </div>
            <div>
              <FormattedText
                displayName='Varav moms'
                formattedName='ticket.price.vat'
              />
              <PriceItem price={ vatPrice } decimals={ 2 } />
            </div>
            <div>
              <FormattedText
                displayName='Varav administrationsavgift'
                formattedName='ticket.price.service'
              />
              <PriceItem price={ servicePrice } decimals={ 2 } />
            </div>
          </div>
        </div>
        <div className='guidelines'>
          <div className='guidelines-title'>{ info.title }</div>
          <ul>
            { info.guidelines.map((g, key) => 
              <li key={ key }>{ g }</li>) }
          </ul>
        </div>
        { seatTickets && seatTickets.length > 0 &&
          <div className='tickets'>
            <div className='tickets-title'>
              <FormattedText
                displayName='Platser'
                formattedName='ticket.seats'
              />
            </div>
            { seatTickets.map((s, key) =>
              <div key={ key } className='ticket'>
                <div className='name'>{ showName }</div>
                { s.showSeatNumber &&
                  (
                    <>
                      <div>
                        <FormattedText
                          displayName='Rad'
                          formattedName='ticket.row'
                        />: { s.rowNbr }
                      </div>
                      <div>
                        <FormattedText
                          displayName='Plats'
                          formattedName='ticket.seat'
                        />: { s.seatNbr }
                      </div>
                    </>
                  )
                }
                <div>
                  <FormattedText
                    displayName='Sektion'
                    formattedName='ticket.section'
                  />: { s.sectionName }
                </div>
                { s.showSeatNumber &&
                  (
                    <div>
                      <FormattedText
                        displayName='Ingång'
                        formattedName='ticket.entrance'
                      />: { s.entranceName }
                    </div>
                  )
                }
                <img
                  alt='Biljett QR-kod'
                  className='barcode-image' 
                  src={ `${ process.env.REACT_APP_API_URL }/api/julius/TicketQR?code=SEBJ ${ s.barcode }` } 
                />
              </div>) 
            }
          </div>
        }
        { packageTickets && packageTickets.length > 0 &&
          <div className='tickets'>
            <div className='tickets-title'>
              <FormattedText
                displayName='Tillval'
                formattedName='ticket.extraitems'
              />
            </div>
            { packageTickets.map((p, key) =>
              <div key={ key } className='ticket'>
                <div className='name'>{ p.name }</div>
                { moment(p.date).toDate().getFullYear() !== 1 &&
                  <div className='show-date'>
                    <FormattedDate 
                      value={ p.date }
                      month='long'
                      day='numeric'
                      year='numeric'
                    />,&nbsp; 
                    <FormattedTime 
                      value={ p.date }
                      hour='numeric'
                      minute='numeric'
                    />
                  </div>
                }
                <br/>
                <div className='additional-info' dangerouslySetInnerHTML={ getAdditionalInfoForPt(p) }/>
                <img
                  alt='Biljett QR-kod'
                  className='barcode-image' 
                  src={ `${ process.env.REACT_APP_API_URL }/api/julius/TicketQR?code=SEBJ ${ p.barcode }` } 
                />
              </div>) 
            }
          </div>
        }
        <div className='welcome'>
          <FormattedText 
            style='welcome-title'
            displayName='Varmt välkomna!'
            formattedName='ticket.welcome'
          />
          <FormattedText 
            style='welcome-text'
            displayName='Önskar Julius Biljettservice'
            formattedName='ticket.fromjulius'  
          />
        </div>
      </div>
    </div>
  );
};

export default TicketPage;


// WEBPACK FOOTER //
// ./src/pages/TicketPage/TicketPage.tsx